import { useTranslation } from 'react-i18next'
import { Empty, Skeleton } from 'antd'
import ActivityChevron from './images/activity-chevron.svg?react'
import { ActivityRow } from './ActivityRow'
import { activityDestination } from './activityTarget'
import type { ActivityItem } from './types'

const PREVIEW_SIZE = 5

interface RecentActivitySectionProps {
  items: ActivityItem[]
  /** 当前空间还没有动态结果时占位，刷新已有结果时不传 */
  skeleton?: boolean
  error?: boolean
  onViewAll: () => void
  onOpenItem: (item: ActivityItem) => void
}

function ActivitySkeleton() {
  const { t } = useTranslation()

  return (
    <div role="status" aria-label={t('common.loading')}>
      {Array.from({ length: PREVIEW_SIZE }, (_, index) => (
        <div
          key={index}
          aria-hidden
          className="flex items-center gap-5 border-b border-[#DCDCDC] p-3 last:border-b-0"
        >
          <Skeleton.Avatar
            active
            shape="square"
            size={42}
            style={{ borderRadius: 8 }}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton.Input
              active
              size="small"
              style={{
                width: 148 + (index % 3) * 28,
                minWidth: 148,
                height: 22,
              }}
            />
            <Skeleton.Input
              active
              size="small"
              style={{
                width: 210 + (index % 2) * 48,
                minWidth: 210,
                height: 18,
              }}
            />
          </div>
          <Skeleton.Input
            active
            size="small"
            style={{ width: 96, minWidth: 96, height: 20 }}
          />
        </div>
      ))}
    </div>
  )
}

function RecentActivitySection({
  items,
  skeleton = false,
  error = false,
  onViewAll,
  onOpenItem,
}: RecentActivitySectionProps) {
  const { t } = useTranslation()
  const preview = items.slice(0, PREVIEW_SIZE)

  return (
    <section
      aria-busy={skeleton || undefined}
      className="rounded-[14px] border border-[#DCDCDC] bg-white/85 p-5 shadow-[2px_2px_30px_0_rgba(0,0,0,0.05)]"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5 py-[6px]">
          <span className="h-3.5 w-0.5 shrink-0 bg-[#6366F1]" />
          <span className="text-base leading-6 font-medium text-black">
            {t('workbench.activityTitle')}
          </span>
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex h-8 cursor-pointer items-center justify-center gap-2 rounded-[6px] border border-[#DCDCDC] bg-transparent px-[18px] text-[13px] leading-5 font-normal text-black backdrop-blur-[6px]"
        >
          {t('workbench.viewAll')}
          <ActivityChevron className="h-2 w-[5px]" aria-hidden />
        </button>
      </div>
      {error ? (
        <Empty description={t('workbench.activityLoadFailed')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : skeleton ? (
        <ActivitySkeleton />
      ) : preview.length === 0 ? (
        <Empty description={t('workbench.activityEmpty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div>
          {preview.map((item) => (
            <ActivityRow
              key={item.id}
              item={item}
              onClick={activityDestination(item) ? () => onOpenItem(item) : undefined}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default RecentActivitySection
